import React, { Fragment, useMemo, useState } from 'react'
import { Grid } from '@swell-ui/Grid'
import { PageTitle } from '@/components/PageTitleOld'
import { StakingUploadDepositCollectionWidget } from '@/components/UploadDepositCollectionWidget'
import { Button } from '@/swell-ui/Button'
import { StakingDepositCollectionSubmissionTable } from '@/components/DepositCollectionSubmissionTable'
import {
  useBatchSubmitAwaitInLoop,
  useDepositSubmissionHistoricState,
  useDepositSubmissionActions,
  useBatchDepositSubmissionStatus,
  BatchDepositSubmissionStatus,
} from '@/state/depositSubmission/hooks'
import { Helmet } from 'react-helmet'
import { useDepositCollectionValidationState } from '@/state/depositValidation/hooks'
import { DepositCollectionValidationSummary } from '@/components/DepositCollectionValidationSummary'
import { DepositCollectionValidationDetail } from '@/components/DepositCollectionValidationDetail'
import { useViewedDepositSubmissionUpdater } from '@/hooks/useViewedDepositSubmissionUpdater'
import useDepositSubmissionTabTitle from '@/hooks/useDepositSubmissionTabTitle'
import { useUserOperatorStatusWithWarning } from '@/hooks/useUserOperatorStatus'

function ValidatorKeys() {
  const { isOperator } = useUserOperatorStatusWithWarning()

  const [textAreaRef, setTextAreaRef] = useState<any>(null)

  const { validationResult } = useDepositCollectionValidationState()

  const { submissionStatus } = useBatchDepositSubmissionStatus()
  const { isHistoric } = useDepositSubmissionHistoricState()
  const { clearSubmissionState } = useDepositSubmissionActions()

  // responsible for indicating that the submission has been viewed
  // (for the sake of showing a notification to users who revisit with an unfinished submission)
  useViewedDepositSubmissionUpdater()

  const onSubmit = useBatchSubmitAwaitInLoop()
  const { tabTitleOverride } = useDepositSubmissionTabTitle()

  type ValidatorKeyView = keyof typeof views

  const view = useMemo<ValidatorKeyView>(() => {
    // Loading initial view based on values from localStorage
    const initialSubmissionStatus = submissionStatus
    return initialSubmissionStatus === BatchDepositSubmissionStatus.UNSTARTED
      ? 'validation'
      : 'submission'
  }, [submissionStatus])

  const onExitSubmission = () => {
    clearSubmissionState()
  }

  const views = {
    validation: (
      <Grid key="validation" container spacing={2}>
        <Grid item xs={12} sm={5}>
          <StakingUploadDepositCollectionWidget
            setTextAreaRef={setTextAreaRef}
            onSubmit={onSubmit}
            preventInteraction={!isOperator}
          />
        </Grid>
        <Grid item xs={12} sm={7}>
          <Grid container direction="column" spacing={2}>
            <Grid item>
              <DepositCollectionValidationSummary
                validationResult={validationResult}
              />
            </Grid>
            <Grid item>
              <DepositCollectionValidationDetail
                validationResult={validationResult}
                textAreaRef={textAreaRef}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    ),
    submission: (
      <Fragment key="submission">
        {!isHistoric && tabTitleOverride && (
          <Helmet>
            <title>{tabTitleOverride}</title>
          </Helmet>
        )}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <StakingDepositCollectionSubmissionTable isHistoric={isHistoric} />
          </Grid>
          <Grid item>
            <Button onClick={onExitSubmission}>Close</Button>
          </Grid>
        </Grid>
      </Fragment>
    ),
  }

  const derived = {
    get pageTitleText() {
      if (isHistoric) return 'Historical submission data'
      return 'Validator keys'
    },
  }

  return (
    <>
      <PageTitle text={derived.pageTitleText} />
      {views[view]}
    </>
  )
}

export { ValidatorKeys }
